"use client";

import { c as _c } from "react/compiler-runtime";
import type React from "react";
import { type FC, memo, type PropsWithChildren } from "react";
export type PropsAreEqualFn<P> = (prevProps: Readonly<PropsWithChildren<P>>, nextProps: Readonly<PropsWithChildren<P>>) => boolean;

/**
 * Create a named context provider with its value provided by a hook
 * @param Context React Context object
 * @param useHook the hook to provide the context value
 * @param propsAreEqual optional comparison function for memoization (like React.memo's second argument)
 * @returns a Component which wraps a context provider
 */
export function createNamedProvider<P, S>(Context: React.Context<S>, useHook: (props: PropsWithChildren<P>) => S, propsAreEqual?: PropsAreEqualFn<P>): FC<PropsWithChildren<P>> {
  const Provider: FC<PropsWithChildren<P>> = props => {
    const $ = _c(3);
    const t0 = useHook(props);
    let t1;
    if ($[0] !== props.children || $[1] !== t0) {
      t1 = <Context.Provider value={t0}>{props.children}</Context.Provider>;
      $[0] = props.children;
      $[1] = t0;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  };
  const displayName = `${Context.displayName}.ProviderWrapper`;

  // Apply memoization if propsAreEqual function is provided
  if (propsAreEqual) {
    const MemoizedProvider = memo(Provider, propsAreEqual);
    MemoizedProvider.displayName = displayName;
    return MemoizedProvider;
  }
  Provider.displayName = displayName;
  return Provider;
}